/* eslint-disable ngrx/on-function-explicit-return-type */
import { createEntityAdapter, EntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { find as _find } from 'lodash';

import * as OneTimePayActions from './one-time-pay-feature.actions';
import { OneTimePaySetup, OTPReviewData } from './one-time-pay-feature.models';

export const oneTimePayFeatureKey = 'oneTimePaySetup';

export interface State extends EntityState<OneTimePaySetup> {
  cancelPathUrl: string;
  operation: string;
  paymentAmtAnalytics: string;
  paymentDateAnalytics: string;
  paymentId: string;
  paymentErrorCode: string;
}

export const adapter: EntityAdapter<OneTimePaySetup> = createEntityAdapter<OneTimePaySetup>({
  selectId: (oneTimePaySetup: OneTimePaySetup) => oneTimePaySetup.billAccountNumber,
  sortComparer: false
});

export const initialState: State = adapter.getInitialState({
  cancelPathUrl: 'overview',
  operation: 'Success',
  paymentAmtAnalytics: '',
  paymentDateAnalytics: '',
  paymentId: '',
  paymentErrorCode: null
});

export const reducer = createReducer(
  initialState,

  on(OneTimePayActions.setOneTimePayCancelPath, (state, action) => ({
    ...state,
    cancelPathUrl: action.url.includes('one-time-pay') ? state.cancelPathUrl : action.url
  })),

  on(OneTimePayActions.setOneTimePayOperation, (state, action) => ({
    ...state,
    operation: action.operation
  })),

  on(OneTimePayActions.setOneTimePayPaymentAmtAnalyticsInfo, (state, action) => ({
    ...state,
    paymentAmtAnalytics: action.paymentAmtForAnalytics
  })),

  on(OneTimePayActions.setOneTimePayPaymentDateAnalyticsInfo, (state, action) => ({
    ...state,
    paymentDateAnalytics: action.paymentDateForAnalytics
  })),

  on(OneTimePayActions.addOneTimePayBillAccount, (state, action) =>
    adapter.setOne(action.setups, state)
  ),

  on(OneTimePayActions.updatePaymentAmount, (state, action) => adapter.upsertOne(action, state)),

  on(OneTimePayActions.removeOneTimePayBillAccount, (state, action) =>
    adapter.removeOne(action.billAccountNumber, {
      ...state
    })
  ),

  on(OneTimePayActions.removeOneTimePayBillAccounts, state => adapter.removeAll(state)),

  on(OneTimePayActions.setSelectedAccounts, (state, action) => ({
    ...state,
    selectedAccounts: action.selectedAccounts
  })),

  on(OneTimePayActions.setOneTimePayReviewInfo, (state, action) =>
    adapter.updateMany(updateOTPData(state, action.reviewData), {
      ...state
    })
  ),

  on(OneTimePayActions.setOneTimePayReviewInfoForEdit, (state, action) =>
    adapter.addMany(action.reviewData as unknown as OneTimePaySetup[], {
      ...state
    })
  ),

  on(OneTimePayActions.storePaymentId, (state, action) => ({
    ...state,
    paymentId: action.paymentId
  })),

  on(OneTimePayActions.storePaymentErrorCode, (state, action) => ({
    ...state,
    paymentErrorCode: action.paymentErrorCode
  })),

  on(OneTimePayActions.resetOneTimePaySetup, () => initialState)
);

export const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors();

/**
 * @returns Updated OTPSetupPayload
 * @param state
 * @param content
 * @description Use the content from the page to upload in the feature state
 */
function updateOTPData(state: State, pageData: OTPReviewData[]): Update<OneTimePaySetup>[] {
  let updationArray: Update<OneTimePaySetup>[] = [];
  for (const OTPitem in state.entities) {
    if (OTPitem) {
      updationArray = [
        ...updationArray,
        buildUpdatePayload(
          state.entities[OTPitem],
          _find(pageData, {
            billAccountNumber: state.entities[OTPitem].billAccountNumber
          })
        )
      ];
    }
  }
  return updationArray;
}

/**
 *
 * @param entity : The current data in the store which needs to be updated
 * @param content : The Content coming from the page
 * @description : Build the payload which would be used to edit the OTP state info.
 */
function buildUpdatePayload(
  entity: OneTimePaySetup,
  content: OTPReviewData
): Update<OneTimePaySetup> {
  return {
    id: entity.billAccountNumber,
    changes: Object.assign({}, entity, { ...content })
  };
}
